import React from 'react';
import {
  Alert,
  Card,
  DatePicker,
  Empty,
  List,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { HistoryOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../services/api';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type HistoryDay = {
  deliveryDate: string;
  poolId: number | null;
  poolStatus: string | null;
  reachedSupplier: boolean;
  statusKey: string;
  statusLabel: string;
  submittedAt: string | null;
  orderId: number | null;
  orderStatus: string | null;
  amount: number;
  items: Array<{
    type: 'dish' | 'combo';
    name: string;
    quantity: number;
    unitPrice: number;
    amount: number;
    addedAt: string;
  }>;
};

const statusColor = (key: string) => {
  switch (key) {
    case 'delivered_to_supplier':
      return 'green';
    case 'awaiting_customer':
      return 'orange';
    case 'draft':
      return 'blue';
    case 'cancelled':
      return 'red';
    default:
      return 'default';
  }
};

const money = (n: number) =>
  `${n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽`;

const MyOrderHistory: React.FC = () => {
  const { user } = useAuthStore();
  const canAccess = user?.role === 'employee' || user?.role === 'customer_manager';

  const [range, setRange] = React.useState<[Dayjs, Dayjs]>([
    dayjs().subtract(29, 'day'),
    dayjs(),
  ]);
  const [loading, setLoading] = React.useState(false);
  const [days, setDays] = React.useState<HistoryDay[]>([]);
  const [meta, setMeta] = React.useState<{ reachedSupplierCount: number; dayCount: number } | null>(
    null
  );

  const load = React.useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const start = range[0].format('YYYY-MM-DD');
      const end = range[1].format('YYYY-MM-DD');
      const resp = await fetch(
        `${API_BASE_URL}/api/daily-menu/my-history?start=${start}&end=${end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        message.error(err?.error || 'Не удалось загрузить историю');
        return;
      }
      const data = await resp.json();
      setDays(data.days || []);
      setMeta({
        reachedSupplierCount: data.reachedSupplierCount ?? 0,
        dayCount: data.dayCount ?? 0,
      });
    } catch {
      message.error('Ошибка соединения');
    } finally {
      setLoading(false);
    }
  }, [canAccess, range]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (!canAccess) {
    return <Alert type="warning" showIcon message="История заказов доступна сотрудникам" />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          <HistoryOutlined /> История заказов
        </Title>
        <Text type="secondary">
          Что вы клали в корзину и дошёл ли заказ до поставщика. «Подтвердить» у сотрудника — это
          сигнал заказчику; поставщику заказ уходит только после отправки ответственным заказчиком.
        </Text>
      </div>

      <Card size="small">
        <Space wrap>
          <RangePicker
            value={range}
            allowClear={false}
            format="DD.MM.YYYY"
            onChange={(v) => {
              if (v?.[0] && v?.[1]) setRange([v[0], v[1]]);
            }}
          />
          {meta && (
            <Text type="secondary">
              Дней с заказами: {meta.dayCount}, до поставщика дошло: {meta.reachedSupplierCount}
            </Text>
          )}
        </Space>
      </Card>

      <Spin spinning={loading}>
        {days.length === 0 ? (
          <Empty description="За выбранный период заказов нет" />
        ) : (
          <List
            dataSource={days}
            renderItem={(day) => (
              <Card
                size="small"
                style={{ marginBottom: 12 }}
                title={dayjs(day.deliveryDate).format('DD.MM.YYYY (dddd)')}
                extra={
                  <Space>
                    <Tag color={statusColor(day.statusKey)}>{day.statusLabel}</Tag>
                    <Text strong>{money(day.amount)}</Text>
                  </Space>
                }
              >
                {day.submittedAt && (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    Вы нажали «Подтвердить»: {dayjs(day.submittedAt).format('DD.MM.YYYY HH:mm')}
                    {day.orderId ? ` · заказ №${day.orderId}` : ''}
                  </Text>
                )}
                {!day.reachedSupplier && day.statusKey !== 'draft' && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message="До поставщика этот заказ не дошёл"
                    description={day.statusLabel}
                  />
                )}
                <List
                  size="small"
                  dataSource={day.items}
                  locale={{ emptyText: 'Детализация блюд недоступна' }}
                  renderItem={(item) => (
                    <List.Item>
                      <Space>
                        <Text>
                          {item.name}
                          {item.type === 'combo' ? ' (комбо)' : ''}
                        </Text>
                        <Tag>x{item.quantity}</Tag>
                        <Text type="secondary">{money(item.amount)}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          />
        )}
      </Spin>
    </Space>
  );
};

export default MyOrderHistory;
